import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Highlight, Text} from "@pg-design/text-module";
import {getAnalyticsSessionId} from "@pg-mono/algolytics";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {replaceDigitsWithZeros} from "../../../credit_calculator/utils/replace_digits_with_zeros";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {useAppSelector} from "../../../store/hooks";
import {getSemanticSearchModalSize} from "../../tracking/get_semantic_search_modal_size";
import {hitAiAssistantOffer} from "../../tracking/semantic_search_gtm_events";
import {trackAmplitudeSemanticSearchSelectItem} from "../../tracking/track_amplitude_semantic_search_select_item";
import {SemanticSearchPropertyDto} from "../../types/SemanticSearchPropertyDto";

interface Props {
    property: SemanticSearchPropertyDto;
    index: number;
}

export function SemanticSearchPropertyListTile(props: Props) {
    const {property, index} = props;
    const {shouldHidePrices} = useShouldHidePrices();
    const modalSize = useAppSelector((state) => getSemanticSearchModalSize(state.semanticSearchModal.surfacePhase));

    const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });

    const imageSrc = property.offer.main_image.m_img_308x256 || property.offer.main_image.m_img_154x128;
    const hasPrice = property.price > 0;
    const priceText = hasPrice ? priceFormat(property.price) || null : null;
    const displayPrice = shouldHidePrices && priceText ? replaceDigitsWithZeros(priceText) : priceText;
    const location = getPropertyLocation(property);
    const floorLabel = getFloorLabel(property.floor);

    return (
        <li className={listItem}>
            <article className={card}>
                <a
                    className={cardLink}
                    href={propertyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Zobacz ofertę: ${property.offer.name}`}
                    onClick={() => {
                        hitAiAssistantOffer("select", property);

                        if (modalSize) {
                            trackAmplitudeSemanticSearchSelectItem({
                                modalSize,
                                analyticSessionId: getAnalyticsSessionId(),
                                item: {itemCategory: "property", property, index}
                            });
                        }
                    }}
                >
                    <div className={imageWrapper}>
                        <Badge variant="label_default" className={investmentBadgeStyle}>
                            <Text variant="info_txt_2" as="span" strong>
                                {property.offer.name}
                            </Text>
                        </Badge>
                        <img className={image} src={imageSrc} alt={property.offer.name} loading="lazy" />
                    </div>

                    <div className={content}>
                        <Text variant="info_txt_1" as="div" className={title}>
                            {property.rooms} {roomsPluralize(property.rooms)}, {areaFormat(property.area)}
                            {floorLabel ? `, ${floorLabel}` : ""}
                        </Text>

                        {displayPrice && (
                            <Text variant="info_txt_1" as="div" className={cx(price, shouldHidePrices && blurredPriceStyle)} aria-hidden={shouldHidePrices}>
                                <Highlight strong>{displayPrice}</Highlight>
                            </Text>
                        )}

                        {location && (
                            <Text variant="info_txt_3" as="div" className={locationText}>
                                {location}
                            </Text>
                        )}
                    </div>
                </a>
            </article>
        </li>
    );
}

function getPropertyLocation(property: SemanticSearchPropertyDto): string {
    const stats = property.offer.region.stats;
    const locationParts = [
        stats?.region_type_city?.name || stats?.region_type_town?.name,
        stats?.region_type_district?.name,
        stats?.region_type_housing_estate?.name
    ].filter((value): value is string => Boolean(value));

    if (locationParts.length > 0) {
        return locationParts.join(", ");
    }

    return property.offer.region.short_name_reverted || property.offer.address;
}

function getFloorLabel(floor: number | null): string {
    if (floor === null) {
        return "";
    }

    if (floor === 0) {
        return "parter";
    }

    return `${floor} piętro`;
}

const listItem = css({
    flex: "0 0 156px",
    scrollSnapAlign: "start",
    minWidth: "156px"
});

const card = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "var(--colors-white)",
    borderRadius: 1,
    boxShadow: "var(--shadow-1)",
    overflow: "hidden"
});

const cardLink = css({
    display: "flex",
    flexDirection: "column",
    color: "inherit",
    textDecoration: "none",
    minHeight: "100%",

    _hover: {
        color: "inherit",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "-2px"
    }
});

const imageWrapper = css({
    position: "relative",
    width: "100%",
    aspectRatio: "154/128",
    backgroundColor: "var(--colors-gray-100)",
    overflow: "hidden"
});

const investmentBadgeStyle = css({
    position: "absolute",
    top: 0.5,
    left: 0.5,
    zIndex: 1,
    maxWidth: "calc(100% - var(--spacing-1))",
    py: 0.5,
    px: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const image = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const content = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    p: 0.5
});

const title = css({
    overflow: "hidden",
    lineClamp: 2,
    lineHeight: "1.25"
});

const price = css({
    color: "var(--colors-secondary)",
    fontWeight: 700
});

const locationText = css({
    color: "var(--colors-gray-700)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const blurredPriceStyle = css({
    filter: "blur(4px)",
    userSelect: "none",
    pointerEvents: "none"
});
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Highlight, Text} from "@pg-design/text-module";
import {getAnalyticsSessionId} from "@pg-mono/algolytics";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {replaceDigitsWithZeros} from "../../../credit_calculator/utils/replace_digits_with_zeros";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {useAppSelector} from "../../../store/hooks";
import {getSemanticSearchModalSize} from "../../tracking/get_semantic_search_modal_size";
import {hitAiAssistantOffer} from "../../tracking/semantic_search_gtm_events";
import {trackAmplitudeSemanticSearchSelectItem} from "../../tracking/track_amplitude_semantic_search_select_item";
import {SemanticSearchPropertyDto} from "../../types/SemanticSearchPropertyDto";

interface Props {
    property: SemanticSearchPropertyDto;
    index: number;
}

export function SemanticSearchPropertyListTile(props: Props) {
    const {property, index} = props;
    const {shouldHidePrices} = useShouldHidePrices();
    const modalSize = useAppSelector((state) => getSemanticSearchModalSize(state.semanticSearchModal.surfacePhase));

    const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });

    const imageSrc = property.offer.main_image.m_img_308x256 || property.offer.main_image.m_img_154x128;
    const hasPrice = property.price > 0;
    const priceText = hasPrice ? priceFormat(property.price) || null : null;
    const displayPrice = shouldHidePrices && priceText ? replaceDigitsWithZeros(priceText) : priceText;
    const location = getPropertyLocation(property);
    const floorLabel = getFloorLabel(property.floor);

    return (
        <li className={listItem}>
            <article className={card}>
                <a
                    className={cardLink}
                    href={propertyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Zobacz ofertę: ${property.offer.name}`}
                    onClick={() => {
                        hitAiAssistantOffer("select", property);

                        if (modalSize) {
                            trackAmplitudeSemanticSearchSelectItem({
                                modalSize,
                                analyticSessionId: getAnalyticsSessionId(),
                                item: {itemCategory: "property", property, index}
                            });
                        }
                    }}
                >
                    <div className={imageWrapper}>
                        <Badge variant="label_default" className={investmentBadgeStyle}>
                            <Text variant="info_txt_2" as="span" strong>
                                {property.offer.name}
                            </Text>
                        </Badge>
                        <img className={image} src={imageSrc} alt={property.offer.name} loading="lazy" />
                    </div>

                    <div className={content}>
                        <Text variant="info_txt_1" as="div" className={title}>
                            {property.rooms} {roomsPluralize(property.rooms)}, {areaFormat(property.area)}
                            {floorLabel ? `, ${floorLabel}` : ""}
                        </Text>

                        {displayPrice && (
                            <Text variant="info_txt_1" as="div" className={cx(price, shouldHidePrices && blurredPriceStyle)} aria-hidden={shouldHidePrices}>
                                <Highlight strong>{displayPrice}</Highlight>
                            </Text>
                        )}

                        {location && (
                            <Text variant="info_txt_3" as="div" className={locationText}>
                                {location}
                            </Text>
                        )}
                    </div>
                </a>
            </article>
        </li>
    );
}

function getPropertyLocation(property: SemanticSearchPropertyDto): string {
    const stats = property.offer.region.stats;
    const locationParts = [
        stats?.region_type_city?.name || stats?.region_type_town?.name,
        stats?.region_type_district?.name,
        stats?.region_type_housing_estate?.name
    ].filter((value): value is string => Boolean(value));

    if (locationParts.length > 0) {
        return locationParts.join(", ");
    }

    return property.offer.region.short_name_reverted || property.offer.address;
}

function getFloorLabel(floor: number | null): string {
    if (floor === null) {
        return "";
    }

    if (floor === 0) {
        return "parter";
    }

    return `${floor} piętro`;
}

const listItem = css({
    flex: "0 0 156px",
    scrollSnapAlign: "start",
    minWidth: "156px"
});

const card = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "var(--colors-white)",
    borderRadius: 1,
    boxShadow: "var(--shadow-1)",
    overflow: "hidden"
});

const cardLink = css({
    display: "flex",
    flexDirection: "column",
    color: "inherit",
    textDecoration: "none",
    minHeight: "100%",

    _hover: {
        color: "inherit",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "-2px"
    }
});

const imageWrapper = css({
    position: "relative",
    width: "100%",
    aspectRatio: "154/128",
    backgroundColor: "var(--colors-gray-100)",
    overflow: "hidden"
});

const investmentBadgeStyle = css({
    position: "absolute",
    top: 0.5,
    left: 0.5,
    zIndex: 1,
    maxWidth: "calc(100% - var(--spacing-1))",
    py: 0.5,
    px: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const image = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const content = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    p: 0.5
});

const title = css({
    overflow: "hidden",
    lineClamp: 2,
    lineHeight: "1.25"
});

const price = css({
    color: "var(--colors-secondary)",
    fontWeight: 700
});

const locationText = css({
    color: "var(--colors-gray-700)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const blurredPriceStyle = css({
    filter: "blur(4px)",
    userSelect: "none",
    pointerEvents: "none"
});
